import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { v4 as uuid } from 'uuid';

import CalendarIcon from '../../assets/images/icon-outline-calendar.svg';
import './flatpickr.scss';

const defaultOptions = {
  enableTime: false,
  minDate: 'today',
  altInput: true,
  altFormat: 'l, j F Y',
  dateFormat: 'Y-m-d',
  disableMobile: true,
  monthSelectorType: 'static',
  inline: false,
  locale: {
    firstDayOfWeek: 1,
  },
};

export const displayDate = (dateObj) => {
  if (dateObj instanceof Date === false) {
    return '';
  }

  const dayAsMonthNumber = dateObj.getDate();
  const normalisedMonthNumber = dateObj.getMonth() + 1;
  const yearAsNumber = dateObj.getFullYear();

  return `${dayAsMonthNumber}/${normalisedMonthNumber}/${yearAsNumber}`;
};

// A custom hook to make it easier to get the necessary props for an datepicker field
export function useDatePickerProps({
  id,
  defaultDate,
  options = {},
  placeholder,
  labelText,
  customInputClasses,
  mutedText,
  mutedLabelText,
}) {
  const [value, setValue] = useState(defaultDate ?? '');
  const [highlightClass, setHighlightClass] = useState('');

  // Note: It's preferred to come up with a non dynamic id to avoid DOM updates
  if (!id) {
    id = uuid();
  }

  return {
    id,
    value,
    setValue,
    options: { ...defaultOptions, ...options },
    placeholder,
    labelText,
    customInputClasses,
    mutedLabelText,
    mutedText,
    highlightClass,
    setHighlightClass,
  };
}

export const DatePicker = ({
  id,
  placeholder,
  labelText,
  value,
  setValue,
  customInputClasses,
  options,
  mutedLabelText,
  mutedText,
  highlightClass,
  holidays,
  upcomingSessions,
  timezone,
  valueChangeCallback,
  icon,
}) => {
  // Use className "custom-select" when you want it to look like a dropdown
  return (
    <section className="flex flex-col">
      {labelText && (
        <div className="pb-2">
          <label htmlFor={id}>{labelText}</label>
          {mutedLabelText && <span className="ml-3 text-xs text-grey-4">{mutedLabelText}</span>}
        </div>
      )}

      <div className={classnames('relative', customInputClasses)}>
        <Flatpickr
          id={id}
          placeholder={placeholder}
          className={classnames(
            customInputClasses,
            // !w-full keeps the input filling this wrapper when customInputClasses includes width utilities (e.g. lg:w-1/2)
            'h-12 !w-full rounded-full border border-grey-3 bg-white px-2 py-2 pr-10 focus:outline-none focus:ring focus:ring-blue-3',
            mutedText && 'mb-3',
            highlightClass
          )}
          data-enable-time
          value={value}
          onChange={(date) => {
            setValue(date);
            valueChangeCallback(date);
          }}
          options={options}
          onDayCreate={(_a, _b, _c, data) => {
            const isHoliday = holidays.some((holiday) =>
              moment(data.dateObj)
                .tz(timezone)
                .isBetween(moment(holiday.startDate).tz(timezone), moment(holiday.endDate).tz(timezone), 'day', [])
            );
            const isUpcomingSession = upcomingSessions.some((session) =>
              moment(data.dateObj)
                .tz(timezone)
                .isBetween(moment(session.startTime).tz(timezone), moment(session.endTime).tz(timezone), 'day', [])
            );
            if (isHoliday) {
              data.innerHTML += '<span class="holiday"></span>';
            }
            if (isUpcomingSession) {
              data.innerHTML += '<span class="session"></span>';
            }
          }}
        />
        <div className="pointer-events-none absolute inset-y-0 right-[10px] top-[10px]">
          {icon || <img src={CalendarIcon} alt="calendar icon" width={24} height={24} />}
        </div>
      </div>
      {mutedText && <span className="text-xs text-grey-4">{mutedText}</span>}
    </section>
  );
};

DatePicker.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.instanceOf(Date))]),
  setValue: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  labelText: PropTypes.string,
  customInputClasses: PropTypes.string,
  options: PropTypes.shape({
    enableTime: PropTypes.bool,
    minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    altInput: PropTypes.bool,
    altFormat: PropTypes.string,
    dateFormat: PropTypes.string,
    disableMobile: PropTypes.bool,
    monthSelectorType: PropTypes.string,
    inline: PropTypes.bool,
    locale: PropTypes.shape({
      firstDayOfWeek: PropTypes.number,
    }),
  }),
  holidays: PropTypes.arrayOf(
    PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      type: PropTypes.string,
      state: PropTypes.string,
      schoolTerm: PropTypes.string,
    })
  ),
  upcomingSessions: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    })
  ),
  timezone: PropTypes.string,
  mutedLabelText: PropTypes.string,
  mutedText: PropTypes.string,
  highlightClass: PropTypes.string,
  valueChangeCallback: PropTypes.func,
  icon: PropTypes.node,
};
DatePicker.defaultProps = {
  value: '',
  labelText: null,
  placeholder: '',
  customInputClasses: '',
  options: defaultOptions,
  holidays: [],
  upcomingSessions: [],
  timezone: '',
  mutedLabelText: null,
  mutedText: null,
  highlightClass: '',
  valueChangeCallback: () => {},
  icon: null,
};
